// 1. Imports
import "./SwipeableModals.scss";
import React, { useEffect, useState, useCallback } from "react";
import SwipeableDrawer from "@mui/material/SwipeableDrawer";
import {
  CircularProgress,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  OutlinedInput,
} from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import BlueButton from "../../../components/BlueButton/BlueButton";
import api from "../../../utils/api";
import { message } from "antd";

const SwipeableModals = ({
  open,
  onClose,
  onOpen,
  currentView,
  user,
  onDataChange,
}) => {
  const [showPassword, setShowPassword] = useState(false);
  const [newUser, setNewUser] = useState(user);
  const [password, setPassword] = useState("");
  const [password_confirmation, setPasswordConfirmation] = useState("");
  const [loading, setIsLoading] = useState(false);

  // Reset state when drawer opens or view changes
  useEffect(() => {
    if (open) {
      setNewUser(user);
      resetPasswordFields();
    }
  }, [open, user, currentView]);

  // Function to reset password fields
  const resetPasswordFields = useCallback(() => {
    setPassword("");
    setPasswordConfirmation("");
    setShowPassword(false);
  }, []);

  // Handle modal close with cleanup
  const handleModalClose = useCallback(() => {
    resetPasswordFields();
    onClose();
  }, [onClose, resetPasswordFields]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      if (currentView === "changePassword") {
        // Only send password data for password change
        if (password_confirmation !== password) {
          message.error("كلمتا المرور غير متطابقتين");
          setIsLoading(false);
          return;
        }
        await api.put(`/users/${newUser.id}`, {
          password: password,
          password_confirmation: password_confirmation,
        });
        message.success("تم تغيير كلمة المرور بنجاح");
      } else if (currentView === "changeVendorPassword") {
        // Handle vendor password change logic here
        if (password_confirmation !== password) {
          message.error("كلمتا المرور غير متطابقتين");
          setIsLoading(false);
          return;
        }
        await api.post(`/update-vendor/${newUser.id}`, {
          password: password,
        });
        message.success("تم تغيير كلمة مرور البائع بنجاح");
      } else if (currentView === "createUser") {
        // Handle create user logic here
        // This would need to be implemented based on your API
        message.success("تم إضافة المستخدم بنجاح");
      } else {
        // Send full user data for user edit
        await api.put(`/users/${newUser.id}`, { ...newUser });
        message.success("تم تعديل المستخدم بنجاح");
      }
      handleModalClose();
      onDataChange();
    } catch (error) {
      message.error("حدث خطأ، يرجى المحاولة مرة أخرى");
    } finally {
      setIsLoading(false);
      resetPasswordFields();
    }
  };

  const handleChange = (e) => {
    setNewUser((prev) => ({
      ...prev,
      [e.target.name]: e.target.value,
    }));
  };

  const handleClickShowPassword = () => setShowPassword((show) => !show);

  const handleMouseDownPassword = (event) => {
    event.preventDefault();
  };

  const renderCreateUserView = () => (
    <div className="user-modal">
      <p className="user-modal-p">اضافة مستخدم جديد</p>
      <form onSubmit={handleSubmit}>
        <FormControl sx={{ width: "100%" }} variant="outlined">
          <InputLabel
            htmlFor="name-input"
            shrink
            sx={{
              background: "white",
              transformOrigin: "right top",
              transform: "translate(14px, -9px) scale(0.75)",
              right: 24,
              left: "auto",
              paddingLeft: "4px",
              paddingRight: "4px",
            }}
          >
            الاسم
          </InputLabel>
          <OutlinedInput
            sx={{ borderRadius: "8px" }}
            required
            id="name-input"
            type="text"
            notched
            name="name"
            onChange={handleChange}
          />
        </FormControl>

        <FormControl sx={{ width: "100%" }} variant="outlined">
          <InputLabel
            htmlFor="phone-input"
            shrink
            sx={{
              background: "white",
              transformOrigin: "right top",
              transform: "translate(14px, -9px) scale(0.75)",
              right: 24,
              left: "auto",
              paddingLeft: "4px",
              paddingRight: "4px",
            }}
          >
            رقم الموبايل
          </InputLabel>
          <OutlinedInput
            sx={{ borderRadius: "8px" }}
            required
            id="phone-input"
            type="text"
            notched
            name="phone"
            onChange={handleChange}
          />
        </FormControl>

        <FormControl sx={{ width: "100%" }} variant="outlined">
          <InputLabel
            htmlFor="email-input"
            shrink
            sx={{
              background: "white",
              transformOrigin: "right top",
              transform: "translate(14px, -9px) scale(0.75)",
              right: 24,
              left: "auto",
              paddingLeft: "4px",
              paddingRight: "4px",
            }}
          >
            البريد الالكتروني
          </InputLabel>
          <OutlinedInput
            sx={{ borderRadius: "8px" }}
            required
            id="email-input"
            type="email"
            notched
            name="email"
            onChange={handleChange}
          />
        </FormControl>

        <FormControl
          sx={{ width: "100%", borderRadius: "8px" }}
          variant="outlined"
        >
          <InputLabel
            htmlFor="password-input"
            sx={{
              left: "auto",
              background: "white",
              right: 12,
              transformOrigin: "right",
              "&:not(.MuiInputLabel-shrink)": {
                transform: "translate(-8px, 12px) scale(1)",
              },
              "&.MuiInputLabel-shrink": {
                transform: "translate(0, -10px) scale(1)",
              },
            }}
          >
            كلمة المرور
          </InputLabel>
          <OutlinedInput
            required
            sx={{ borderRadius: "8px" }}
            id="password-input"
            type={showPassword ? "text" : "password"}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            name="password"
            endAdornment={
              <InputAdornment position="end">
                <IconButton
                  aria-label="toggle password visibility"
                  onClick={handleClickShowPassword}
                  onMouseDown={handleMouseDownPassword}
                  edge="end"
                >
                  {showPassword ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            }
          />
        </FormControl>

        <FormControl sx={{ width: "100%" }} variant="outlined">
          <InputLabel
            htmlFor="confirm-password-input"
            sx={{
              left: "auto",
              background: "white",
              right: 12,
              transformOrigin: "right",
              "&:not(.MuiInputLabel-shrink)": {
                transform: "translate(-8px, 12px) scale(1)",
              },
              "&.MuiInputLabel-shrink": {
                transform: "translate(0, -10px) scale(1)",
              },
            }}
          >
            تاكيد كلمة المرور
          </InputLabel>
          <OutlinedInput
            sx={{ borderRadius: "8px" }}
            required
            id="confirm-password-input"
            type={showPassword ? "text" : "password"}
            value={password_confirmation}
            onChange={(e) => setPasswordConfirmation(e.target.value)}
            name="password_confirmation"
            endAdornment={
              <InputAdornment position="end">
                <IconButton
                  aria-label="toggle password visibility"
                  onClick={handleClickShowPassword}
                  onMouseDown={handleMouseDownPassword}
                  edge="end"
                >
                  {showPassword ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            }
          />
        </FormControl>

        <BlueButton
          text="اضافة"
          size="medium"
          variant="fill"
          type="submit"
          loading={loading}
        />
      </form>
    </div>
  );

  const renderChangePasswordView = () => (
    <div className="user-modal">
      <p className="user-modal-p">تعديل كلمة المرور</p>
      <form onSubmit={handleSubmit}>
        <FormControl
          sx={{ width: "100%", borderRadius: "8px" }}
          variant="outlined"
        >
          <InputLabel
            htmlFor="new-password-input"
            sx={{
              left: "auto",
              background: "white",
              right: 12,
              transformOrigin: "right",
              "&:not(.MuiInputLabel-shrink)": {
                transform: "translate(-8px, 12px) scale(1)",
              },
              "&.MuiInputLabel-shrink": {
                transform: "translate(0, -10px) scale(0.8)",
              },
            }}
          >
            كلمة المرور الجديدة
          </InputLabel>
          <OutlinedInput
            sx={{ borderRadius: "8px" }}
            id="new-password-input"
            type={showPassword ? "text" : "password"}
            value={password}
            endAdornment={
              <InputAdornment position="end">
                <IconButton
                  aria-label="toggle password visibility"
                  onClick={handleClickShowPassword}
                  onMouseDown={handleMouseDownPassword}
                  edge="end"
                >
                  {showPassword ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            }
            required
            name="password"
            onChange={(e) => setPassword(e.target.value)}
          />
        </FormControl>

        <FormControl sx={{ width: "100%" }} variant="outlined">
          <InputLabel
            htmlFor="confirm-new-password-input"
            sx={{
              left: "auto",
              background: "white",
              right: 12,
              transformOrigin: "right",
              "&:not(.MuiInputLabel-shrink)": {
                transform: "translate(-8px, 12px) scale(1)",
              },
              "&.MuiInputLabel-shrink": {
                transform: "translate(0, -10px) scale(0.8)",
              },
            }}
          >
            تاكيد كلمة المرور
          </InputLabel>
          <OutlinedInput
            sx={{ borderRadius: "8px" }}
            id="confirm-new-password-input"
            type={showPassword ? "text" : "password"}
            value={password_confirmation}
            endAdornment={
              <InputAdornment position="end">
                <IconButton
                  aria-label="toggle password visibility"
                  onClick={handleClickShowPassword}
                  onMouseDown={handleMouseDownPassword}
                  edge="end"
                >
                  {showPassword ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            }
            required
            name="password_confirmation"
            onChange={(e) => setPasswordConfirmation(e.target.value)}
          />
        </FormControl>

        <BlueButton
          text="تاكيد"
          size="medium"
          variant="fill"
          type="submit"
          loading={loading}
        />
      </form>
    </div>
  );

  const renderEditUserView = () => (
    <div className="user-modal">
      <p className="user-modal-p"> تعديل المستخدم</p>
      <form onSubmit={handleSubmit}>
        <FormControl sx={{ width: "100%" }} variant="outlined">
          <InputLabel
            htmlFor="edit-name-input"
            shrink
            sx={{
              background: "white",
              transformOrigin: "right top",
              transform: "translate(14px, -9px) scale(0.8)",
              right: 24,
              left: "auto",
              paddingLeft: "4px",
              paddingRight: "4px",
            }}
          >
            الاسم
          </InputLabel>
          <OutlinedInput
            sx={{ borderRadius: "8px" }}
            required
            id="edit-name-input"
            type="text"
            notched
            value={newUser?.name || ""}
            onChange={handleChange}
            name="name"
          />
        </FormControl>

        <FormControl sx={{ width: "100%" }} variant="outlined">
          <InputLabel
            htmlFor="edit-phone-input"
            shrink
            sx={{
              background: "white",
              transformOrigin: "right top",
              transform: "translate(14px, -9px) scale(0.8)",
              right: 24,
              left: "auto",
              paddingLeft: "4px",
              paddingRight: "4px",
            }}
          >
            رقم الموبايل
          </InputLabel>
          <OutlinedInput
            sx={{ borderRadius: "8px" }}
            required
            id="edit-phone-input"
            type="text"
            notched
            value={newUser?.phone || ""}
            onChange={handleChange}
            name="phone"
          />
        </FormControl>

        <FormControl sx={{ width: "100%" }} variant="outlined">
          <InputLabel
            htmlFor="edit-email-input"
            shrink
            sx={{
              background: "white",
              transformOrigin: "right top",
              transform: "translate(14px, -9px) scale(0.8)",
              right: 24,
              left: "auto",
              paddingLeft: "4px",
              paddingRight: "4px",
            }}
          >
            البريد الالكتروني
          </InputLabel>
          <OutlinedInput
            sx={{ borderRadius: "8px" }}
            required
            id="edit-email-input"
            type="email"
            notched
            value={newUser?.email || ""}
            onChange={handleChange}
            name="email"
          />
        </FormControl>

        <BlueButton
          text={loading ? "" : "حفظ"}
          icon={
            loading ? (
              <CircularProgress
                color="success"
                className="circular-progress"
                size={24}
              />
            ) : (
              ""
            )
          }
          size="medium"
          variant="fill"
          loading={loading}
          type="submit"
        />
      </form>
    </div>
  );

  const renderDefaultView = () => <div></div>;

  const views = {
    createUser: renderCreateUserView,
    editUser: renderEditUserView,
    changePassword: renderChangePasswordView,
    changeVendorPassword: renderChangePasswordView,
  };

  return (
    <SwipeableDrawer
      anchor="left"
      open={open}
      onClose={handleModalClose}
      onOpen={onOpen}
      sx={{ zIndex: "1202" }}
      disableScrollLock
    >
      {(views[currentView] || renderDefaultView)()}
    </SwipeableDrawer>
  );
};

export default SwipeableModals;
